import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Lock, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="reset-password-page">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Invalid Reset Link</h2>
          <p className="text-slate-600 mb-6">This password reset link is invalid or has expired.</p>
          <Button onClick={() => navigate("/")} data-testid="go-home-btn">Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="reset-password-page">
      <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full mx-4">
        {success ? (
          <div className="text-center" data-testid="reset-success">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Password Reset</h2>
            <p className="text-slate-600 mb-6">Your password has been reset. You can now sign in.</p>
            <Button className="w-full btn-primary" onClick={() => navigate("/")} data-testid="go-login-btn">
              Go to Sign In
            </Button>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">Set New Password</h2>
            <p className="text-slate-600 text-sm mb-6 text-center">Enter your new password below.</p>
            <form onSubmit={handleReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="new-password" type="password" placeholder="Min. 8 characters" className="pl-10" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="input-new-password" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="confirm-password" type="password" placeholder="Re-enter password" className="pl-10" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} data-testid="input-confirm-password" />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary h-11" disabled={loading} data-testid="reset-password-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Reset Password
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
